import React, { useState } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  Typography,
  Box,
  Divider,
  Button,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
} from '@mui/material';
import {
  Notifications,
  Message,
  Home,
  Info,
  Event,
  MarkEmailRead,
  Delete,
  Clear
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';

const NotificationDropdown: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const {
    state,
    markAsRead,
    deleteNotification,
    clearAllNotifications
  } = useNotifications();

  // Ensure handleClick is always defined and memoized
  const safeHandleClick = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    try {
      console.log('NotificationDropdown handleClick called', { event, anchorEl });
      setAnchorEl(event.currentTarget);
    } catch (error) {
      console.error('Error in handleClick:', error);
      // Fallback: ensure anchorEl is set to prevent onClick errors
      setAnchorEl(event.currentTarget);
    }
  }, [anchorEl]);

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleNotificationClick = React.useCallback((notification: any) => {
    if (markAsRead) {
      markAsRead([notification.id]);
    }
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
    handleClose();
  }, [markAsRead, navigate, handleClose]);

  const handleMarkAllAsRead = React.useCallback(() => {
    if (markAsRead && state?.notifications) {
      const unreadIds = state.notifications.filter(n => !n.isRead).map(n => n.id);
      markAsRead(unreadIds);
    }
  }, [markAsRead, state?.notifications]);

  // Ensure all required functions are available before rendering
  if (!markAsRead || !deleteNotification || !clearAllNotifications || !state) {
    console.warn('NotificationDropdown: Required functions not available yet');
    return null; // Don't render until everything is ready
  }

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'message':
        return <Message color="primary" />;
      case 'property':
        return <Home color="secondary" />;
      case 'booking':
        return <Event color="success" />;
      default:
        return <Info color="info" />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  return (
    <>
      <IconButton 
        color="inherit" 
        onClick={(event) => {
          try {
            safeHandleClick(event);
          } catch (error) {
            console.error('Error in notification button onClick:', error);
            // Fallback: just set the anchor element
            setAnchorEl(event.currentTarget);
          }
        }}
      >
        <Badge badgeContent={state.unreadCount} color="error">
          <Notifications />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: { width: 400, maxHeight: 600 }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Notifications</Typography>
            {state.unreadCount > 0 && (
              <Button size="small" onClick={handleMarkAllAsRead} startIcon={<MarkEmailRead />}>
                Mark all read
              </Button>
            )}
          </Box>
          {state.notifications.length > 0 && (
            <Button 
              size="small" 
              color="error" 
              onClick={clearAllNotifications}
              startIcon={<Clear />}
              sx={{ mt: 1 }}
            >
              Clear all
            </Button>
          )}
        </Box>

        {state.notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">No notifications</Typography>
          </Box>
        ) : (
          <List sx={{ p: 0, maxHeight: 400, overflow: 'auto' }}>
            {state.notifications.slice(0, 10).map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  sx={{
                    bgcolor: notification.isRead ? 'transparent' : 'action.hover',
                    '&:hover': { bgcolor: 'action.selected' }
                  }}
                >
                  <ListItemButton 
                    onClick={() => handleNotificationClick(notification)}
                    sx={{ p: 0, width: '100%' }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'transparent' }}>
                        {getNotificationIcon(notification.type)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Typography component="span" variant="subtitle2" sx={{ fontWeight: notification.isRead ? 'normal' : 'bold' }}>
                            {notification.title}
                          </Typography>
                          <Typography component="span" variant="caption" color="text.secondary">
                            {formatTimestamp(notification.timestamp)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography component="span" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            {notification.message}
                          </Typography>
                          {notification.metadata?.senderName && (
                            <Typography component="span" variant="caption" color="text.secondary">
                              From: {notification.metadata.senderName}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteNotification(notification.id);
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </ListItem>
                {index < state.notifications.slice(0, 10).length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}

        {state.notifications.length > 10 && (
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', textAlign: 'center' }}>
            <Button onClick={() => { navigate('/notifications'); handleClose(); }}>
              View all notifications
            </Button>
          </Box>
        )}
      </Menu>
    </>
  );
};

export default NotificationDropdown; 